/**
 * LoginPage — magic link authentication (primary) with optional
 * email + password sign-in.
 * Portal: Customer Portal
 *
 * There is deliberately NO access-code sign-in path here. It was removed in
 * db93e48 (2026-09-16) and silently reintroduced by 8b476dc (2026-09-17); this
 * removes it again. It signed in as a single hardcoded account with the typed
 * code as the password, which is an unthrottled password-guessing surface
 * aimed at one known user. Do not reintroduce it.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AuthCard } from '../components/AuthCard';

type Mode = 'magic-link' | 'password';


function formatAuthError(message: string | undefined): string {
  if (!import.meta.env.VITE_SUPABASE_URL) {
    return 'Portal is not connected to Supabase. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in apps/customer-portal/.env.local.';
  }
  if (!message || /failed to fetch/i.test(message) || /networkerror/i.test(message)) {
    return 'Could not reach the portal backend. Confirm VITE_SUPABASE_URL is set and the Supabase project is reachable.';
  }
  if (/invalid login credentials/i.test(message)) {
    return 'Invalid email or password.';
  }
  return message;
}

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState<Mode>('magic-link');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(() =>
    params.get('auth_error') === 'callback'
      ? 'This sign-in link could not be verified. It may have expired or already been used. Request a new link to continue.'
      : null,
  );
  const [loading, setLoading] = useState(false);

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setPassword('');
  };

  const handleMagicLink = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error: authError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });
      if (authError) {
        setError(formatAuthError(authError.message));
      } else {
        setSent(true);
      }
    } catch (err) {
      setError(formatAuthError(err instanceof Error ? err.message : undefined));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (authError) {
        setError(formatAuthError(authError.message));
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(formatAuthError(err instanceof Error ? err.message : undefined));
    } finally {
      setLoading(false);
    }
  };


  const onSubmit = mode === 'magic-link' ? handleMagicLink : handlePasswordSignIn;

  const submitDisabled =
    loading ||
    (mode === 'magic-link' && !email) ||
    (mode === 'password' && (!email || !password));

  return (
    <AuthCard>
      {sent ? (
        <div className="text-center py-2">
          <p className="text-sm font-semibold" style={{ color: '#1F1E26' }}>
            Check your email
          </p>
          <p className="text-sm mt-1" style={{ color: '#6B6A75' }}>
            We sent a sign-in link to{' '}
            <span className="font-medium" style={{ color: '#1F1E26' }}>{email}</span>
          </p>
          <button
            onClick={() => { setSent(false); setEmail(''); }}
            className="mt-4 text-sm underline"
            style={{ color: '#8E8CB3' }}
          >
            Use a different email
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <h1 className="text-lg font-semibold" style={{ color: '#1F1E26' }}>
              Sign in to your portal
            </h1>
          </div>

          <div>
            <label htmlFor="email" className="public-label">
              Email address
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="public-input"
            />
          </div>

          {mode === 'password' && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="public-label" style={{ marginBottom: 0 }}>
                  Password
                </label>
                <Link to="/forgot-password" className="text-xs underline" style={{ color: '#8E8CB3' }}>
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="public-input"
              />
            </div>
          )}


          {error && (
            <div className="public-error">
              <p>{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitDisabled}
            className="public-btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? mode === 'magic-link' ? 'Sending...' : 'Signing in...'
              : mode === 'magic-link'
                ? 'Send sign-in link'
                : 'Sign in'}
          </button>

          <div className="space-y-2 text-center">
            {mode !== 'password' && (
              <p>
                <button
                  type="button"
                  onClick={() => switchMode('password')}
                  className="text-xs underline"
                  style={{ color: '#8E8CB3' }}
                >
                  Sign in with password instead
                </button>
              </p>
            )}
            {mode !== 'magic-link' && (
              <p>
                <button
                  type="button"
                  onClick={() => switchMode('magic-link')}
                  className="text-xs underline"
                  style={{ color: '#8E8CB3' }}
                >
                  Use a sign-in link instead
                </button>
              </p>
            )}
          </div>

        </form>
      )}
    </AuthCard>
  );
}
